import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon, BanknotesIcon, BoltIcon, ChartBarIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Card,
  CardBody,
  CardHeader,
  StatsCards,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../components/ui';

type Payment = {
  id: number;
  operation_token?: string | null;
  shop_process_id?: string | null;
  amount: number;
  currency: string;
  status: string;
  customer_name?: string;
  provider_tx_id?: string;
  created_at: string;
  processed_at?: string;
};

const paymentExternalId = (payment: Payment) => payment.shop_process_id || payment.operation_token || null;
const paymentDisplayId = (payment: Payment) => paymentExternalId(payment) || String(payment.id);

type Withdrawal = {
  id: number;
  external_trx: string;
  amount: number;
  currency: string;
  status: string;
  account: string;
  payway?: string;
  created_at: string;
  processed_at?: string;
  expires_at?: string;
  processed_by_username?: string;
};

type Operator = {
  id: number;
  username: string;
  is_active: boolean;
};

const paymentStatusTone: Record<string, Parameters<typeof StatusBadge>[0]['tone']> = {
  pending: 'warning',
  confirmed: 'success',
  rejected: 'danger',
  cancelled: 'default',
};

const paymentStatusText: Record<string, string> = {
  pending: 'Ожидает',
  confirmed: 'Подтвержден',
  rejected: 'Отклонен',
  cancelled: 'Отменен',
};

const withdrawalStatusTone: Record<string, Parameters<typeof StatusBadge>[0]['tone']> = {
  pending: 'warning',
  submitted: 'warning',
  processing: 'info',
  success: 'success',
  failed: 'danger',
  failure: 'danger', // legacy
  cancelled: 'default',
  expired: 'default',
};

const withdrawalStatusText: Record<string, string> = {
  pending: 'Ожидает',
  submitted: 'Отправлен оператору',
  processing: 'В обработке',
  success: 'Выполнен',
  failed: 'Отклонен',
  failure: 'Отклонен', // legacy
  cancelled: 'Отменен',
  expired: 'Истек',
};

const normalizeWithdrawalStatus = (status: string) => (status === 'failure' ? 'failed' : status);

const normalizeWithdrawal = (withdrawal: Withdrawal): Withdrawal => ({
  ...withdrawal,
  status: normalizeWithdrawalStatus(withdrawal.status),
});

const finalWithdrawStatuses: Array<Withdrawal['status']> = ['success', 'failed', 'cancelled'];

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const amountWithCurrency = (amount: number, currency: string) =>
  `${amount.toLocaleString('ru-RU')} ${currency}`;

const maskAccount = (account: string) => {
  if (!account) {
    return '—';
  }
  const trimmed = account.replace(/\s+/g, '');
  if (trimmed.length <= 8) return account;
  return `${trimmed.slice(0, 4)} •••• ${trimmed.slice(-4)}`;
};

const isExpired = (withdrawal: Withdrawal): boolean => {
  const status = normalizeWithdrawalStatus(withdrawal.status);
  if (finalWithdrawStatuses.includes(status)) {
    return false;
  }
  if (withdrawal.expires_at) {
    const expMs = Date.parse(withdrawal.expires_at);
    if (Number.isFinite(expMs)) return Date.now() >= expMs;
  }
  const createdMs = Date.parse(withdrawal.created_at);
  if (!Number.isFinite(createdMs)) return false;
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  return Date.now() - createdMs > ONE_DAY_MS;
};

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user?.role !== 'admin') {
    return <div className="text-center text-red-600">Доступ запрещен</div>;
  }

  const paymentsQuery = useQuery<Payment[]>({
    queryKey: ['dashboard', 'payments'],
    queryFn: async () => {
      const { data } = await api.get('/admin/payments');
      return (data || []) as Payment[];
    },
    staleTime: 30_000,
  });

  const withdrawalsQuery = useQuery<Withdrawal[]>({
    queryKey: ['dashboard', 'withdrawals'],
    queryFn: async () => {
      const { data } = await api.get('/admin/withdrawals');
      return ((data || []) as Withdrawal[]).map(normalizeWithdrawal);
    },
    staleTime: 30_000,
  });

  const operatorsQuery = useQuery<Operator[]>({
    queryKey: ['dashboard', 'operators'],
    queryFn: async () => {
      const { data } = await api.get('/admin/operators');
      return (data || []) as Operator[];
    },
    staleTime: 60_000,
  });

  const payments = paymentsQuery.data ?? [];
  const withdrawals = withdrawalsQuery.data ?? [];
  const operators = operatorsQuery.data ?? [];

  const pendingPayments = payments.filter((payment) => payment.status === 'pending');
  const confirmedToday = payments.filter((payment) => {
    if (payment.status !== 'confirmed') return false;
    const ts = payment.processed_at ?? payment.created_at;
    return new Date(ts).toDateString() === new Date().toDateString();
  });
  const rejectedPayments = payments.filter((payment) => payment.status === 'rejected');

  const pendingWithdrawals = withdrawals.filter((withdrawal) => withdrawal.status === 'pending');
  const expiredWithdrawals = withdrawals.filter(isExpired);
  const successWithdrawalsToday = withdrawals.filter((withdrawal) => {
    if (withdrawal.status !== 'success') return false;
    const ts = withdrawal.processed_at ?? withdrawal.created_at;
    return new Date(ts).toDateString() === new Date().toDateString();
  });

  const activeOperators = operators.filter((operator) => operator.is_active);

  const statsData = useMemo(
    () => [
      {
        key: 'withdrawals_queue',
        title: 'Выводы в очереди',
        amount: `${pendingWithdrawals.length}`,
        subtitle:
          expiredWithdrawals.length > 0
            ? `Просрочено: ${expiredWithdrawals.length}`
            : 'Просроченных заявок нет',
        variant: expiredWithdrawals.length > 0 ? ('warning' as const) : ('success' as const),
        icon: <BoltIcon className="h-6 w-6" />,
      },
      {
        key: 'payments_pending',
        title: 'Платежи в ожидании',
        amount: `${pendingPayments.length}`,
        subtitle:
          confirmedToday.length > 0
            ? `Подтверждено сегодня: ${confirmedToday.length}`
            : 'Новых подтверждений пока нет',
        variant: pendingPayments.length > 0 ? ('primary' as const) : ('success' as const),
        icon: <BanknotesIcon className="h-6 w-6" />,
      },
      {
        key: 'operators',
        title: 'Операторы',
        amount: `${activeOperators.length}`,
        subtitle: `Всего: ${operators.length}`,
        variant: activeOperators.length ? ('success' as const) : ('neutral' as const),
        icon: <UsersIcon className="h-6 w-6" />,
      },
      {
        key: 'alerts',
        title: 'Контроль рисков',
        amount: `${expiredWithdrawals.length + rejectedPayments.length}`,
        subtitle: `Просрочено выводов: ${expiredWithdrawals.length} • Отклонено платежей: ${rejectedPayments.length}`,
        variant: rejectedPayments.length || expiredWithdrawals.length ? ('warning' as const) : ('neutral' as const),
        icon: <ChartBarIcon className="h-6 w-6" />,
      },
    ],
    [
      pendingWithdrawals.length,
      expiredWithdrawals.length,
      pendingPayments.length,
      confirmedToday.length,
      activeOperators.length,
      operators.length,
      rejectedPayments.length,
    ]
  );

  const latestPayments = useMemo(
    () =>
      [...payments]
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
        .slice(0, 5),
    [payments]
  );

  const latestWithdrawals = useMemo(
    () =>
      [...withdrawals]
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
        .slice(0, 5),
    [withdrawals]
  );

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase text-secondary-500">Сводка магазина</p>
          <h1 className="text-2xl font-semibold text-[var(--app-heading)]">
            {user.shopName ? `Магазин «${user.shopName}»` : 'Панель администратора'}
          </h1>
          <p className="text-sm text-[var(--app-text-muted)]">
            Следите за ключевыми показателями магазина и переходите к действиям в один клик.
          </p>
        </div>
        <StatsCards data={statsData} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Последние платежи"
              description="Пять последних входящих платежей магазина."
              actions={
                <button
                  onClick={() => navigate('/payments')}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 transition hover:text-primary-700"
                >
                  Открыть все <ArrowRightIcon className="h-4 w-4" />
                </button>
              }
            />
            <CardBody className="space-y-2">
              <div className="grid gap-3 md:hidden">
                {paymentsQuery.isLoading ? (
                  <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-[var(--app-text-muted)]">
                    Загружаем данные по платежам...
                  </div>
                ) : latestPayments.length === 0 ? (
                  <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-[var(--app-text-muted)]">
                    Платежи еще не поступали.
                  </div>
                ) : (
                  latestPayments.map((payment) => (
                    <div
                      key={payment.id}
                      className="space-y-3 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-4 shadow-sm transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[var(--app-heading)]">#{paymentDisplayId(payment)}</p>
                          <p className="text-xs text-[var(--app-text-muted)]">
                            {payment.customer_name || 'Покупатель не указан'}
                          </p>
                        </div>
                        <StatusBadge tone={paymentStatusTone[payment.status] || 'default'}>
                          {paymentStatusText[payment.status] || payment.status}
                        </StatusBadge>
                      </div>
                      <div className="space-y-1 text-sm text-[var(--app-text)]">
                        <p className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)] whitespace-nowrap">
                          {amountWithCurrency(payment.amount, payment.currency)}
                        </p>
                        {payment.provider_tx_id ? (
                          <p className="text-xs text-[var(--app-text-muted)]">
                            Транзакция: {payment.provider_tx_id}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-x-4 text-xs text-[var(--app-text-muted)]">
                        <span className="text-[var(--app-text-muted)]">
                          Создан: {formatDateTime(payment.created_at)}
                        </span>
                        {payment.processed_at ? (
                          <span className="text-[var(--app-text-muted)]">
                            Обработан: {formatDateTime(payment.processed_at)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="hidden md:block">
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>ID / Покупатель</TableHeaderCell>
                        <TableHeaderCell>Сумма</TableHeaderCell>
                        <TableHeaderCell>Статус</TableHeaderCell>
                        <TableHeaderCell>Создан</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentsQuery.isLoading ? (
                        <TableRow>
                  <TableCell colSpan={4} className="text-center text-[var(--app-text-muted)]">
                            Загружаем данные по платежам...
                          </TableCell>
                        </TableRow>
                      ) : latestPayments.length === 0 ? (
                        <TableRow>
                  <TableCell colSpan={4} className="text-center text-[var(--app-text-muted)]">
                            Платежи еще не поступали.
                          </TableCell>
                        </TableRow>
                      ) : (
                        latestPayments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>
                              <div className="space-y-1 text-sm">
                                <p className="font-semibold text-[var(--app-heading)]">#{paymentDisplayId(payment)}</p>
                                <p className="text-xs text-[var(--app-text-muted)]">
                                  {payment.customer_name || 'Не указан'}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1 text-sm text-[var(--app-text)]">
                                <div className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)] whitespace-nowrap">
                                  {amountWithCurrency(payment.amount, payment.currency)}
                                </div>
                                {payment.provider_tx_id ? (
                                  <div className="text-xs text-[var(--app-text-muted)]">
                                    Транзакция: {payment.provider_tx_id}
                                  </div>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell>
                              <StatusBadge tone={paymentStatusTone[payment.status] || 'default'}>
                                {paymentStatusText[payment.status] || payment.status}
                              </StatusBadge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-[var(--app-text-muted)]">
                                {formatDateTime(payment.created_at)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Последние выводы"
              description="Последние операции на вывод средств."
              actions={
                <button
                  onClick={() => navigate('/withdrawals')}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 transition hover:text-primary-700"
                >
                  Открыть все <ArrowRightIcon className="h-4 w-4" />
                </button>
              }
            />
            <CardBody className="space-y-2">
              <div className="grid gap-3 md:hidden">
                {withdrawalsQuery.isLoading ? (
                  <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-[var(--app-text-muted)]">
                    Загружаем данные по выводам...
                  </div>
                ) : latestWithdrawals.length === 0 ? (
                  <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-[var(--app-text-muted)]">
                    Заявок на вывод пока нет.
                  </div>
                ) : (
                  latestWithdrawals.map((withdrawal) => (
                    <div
                      key={withdrawal.id}
                      className="space-y-3 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-4 shadow-sm transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[var(--app-heading)]">#{withdrawal.id}</p>
                          <p className="text-xs text-[var(--app-text-muted)]">
                            Трекер: {withdrawal.external_trx || '—'}
                          </p>
                        </div>
                        <div className="space-y-1 text-right">
                          <StatusBadge tone={withdrawalStatusTone[withdrawal.status] || 'default'}>
                            {withdrawalStatusText[withdrawal.status] || withdrawal.status}
                          </StatusBadge>
                          {isExpired(withdrawal) ? (
                            <span className="inline-flex rounded-full bg-danger/10 px-2 py-0.5 text-[11px] font-semibold text-danger">
                              Просрочено
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-1 text-sm text-[var(--app-text)]">
                        <p className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)] whitespace-nowrap">
                          {amountWithCurrency(withdrawal.amount, withdrawal.currency)}
                        </p>
                        {withdrawal.payway ? (
                          <p className="text-xs text-[var(--app-text-muted)]">Канал: {withdrawal.payway}</p>
                        ) : null}
                        <p className="text-xs text-[var(--app-text-muted)]">
                          Реквизиты: {maskAccount(withdrawal.account)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-x-4 text-xs text-[var(--app-text-muted)]">
                        <span className="text-[var(--app-text-muted)]">
                          Создан: {formatDateTime(withdrawal.created_at)}
                        </span>
                        {withdrawal.processed_at ? (
                          <span className="text-[var(--app-text-muted)]">
                            Обработан: {formatDateTime(withdrawal.processed_at)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="hidden md:block">
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>ID / Трекер</TableHeaderCell>
                        <TableHeaderCell>Сумма</TableHeaderCell>
                        <TableHeaderCell>Статус</TableHeaderCell>
                        <TableHeaderCell>Реквизиты</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {withdrawalsQuery.isLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-[var(--app-text-muted)]">
                            Загружаем данные по выводам...
                          </TableCell>
                        </TableRow>
                      ) : latestWithdrawals.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-[var(--app-text-muted)]">
                            Заявок на вывод пока нет.
                          </TableCell>
                        </TableRow>
                      ) : (
                        latestWithdrawals.map((withdrawal) => (
                          <TableRow key={withdrawal.id}>
                            <TableCell>
                              <div className="space-y-1 text-sm">
                                <p className="font-semibold text-[var(--app-heading)]">#{withdrawal.id}</p>
                                <p className="text-xs text-[var(--app-text-muted)]">
                                  Трекер: {withdrawal.external_trx || '—'}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1 text-sm text-[var(--app-text)]">
                                <div className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)] whitespace-nowrap">
                                  {amountWithCurrency(withdrawal.amount, withdrawal.currency)}
                                </div>
                                {withdrawal.payway ? (
                                  <div className="text-xs text-[var(--app-text-muted)]">Канал: {withdrawal.payway}</div>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <StatusBadge tone={withdrawalStatusTone[withdrawal.status] || 'default'}>
                                  {withdrawalStatusText[withdrawal.status] || withdrawal.status}
                                </StatusBadge>
                                {isExpired(withdrawal) ? (
                                  <span className="inline-flex rounded-full bg-danger/10 px-2 py-0.5 text-[11px] font-semibold text-danger">
                                    Просрочено
                                  </span>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1 text-xs text-[var(--app-text-muted)]">
                                <p>Создан: {formatDateTime(withdrawal.created_at)}</p>
                                {withdrawal.processed_at ? (
                                  <p>Обработан: {formatDateTime(withdrawal.processed_at)}</p>
                                ) : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader
              title="Быстрые действия"
              description="Открывайте ключевые разделы панели администратора."
            />
            <CardBody className="space-y-3">
              <button
                onClick={() => navigate('/payments')}
                className="flex w-full items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-primary-200 hover:text-primary-600"
              >
                Перейти к платежам
                <ArrowRightIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/withdrawals')}
                className="flex w-full items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-primary-200 hover:text-primary-600"
              >
                Открыть выводы
                <ArrowRightIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/operators')}
                className="flex w-full items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-primary-200 hover:text-primary-600"
              >
                Управление операторами
                <ArrowRightIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/shop-settings')}
                className="flex w-full items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-primary-200 hover:text-primary-600"
              >
                Настройки магазина
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
